/* ============================================================================
   FIGS Motion runtime
   ----------------------------------------------------------------------------
   Bridges src/motion.css to GSAP so a CSS transition and a GSAP tween with the
   same token name behave identically. Every experiment imports this and reads
   durations/easings from MOTION — never a literal number.

   GSAP + ScrollTrigger are loaded from a pinned CDN in each page's <head>, so
   experiment pages also run with zero install (open the .html directly, or
   `npx serve`). Swap to `import gsap from 'gsap'` when a pattern graduates
   into the production codebase.
   ========================================================================= */

/** Read a CSS custom property as a number of seconds (GSAP's unit). */
function seconds(css, token) {
  const raw = css.getPropertyValue(token).trim();
  if (raw.endsWith('ms')) return parseFloat(raw) / 1000;
  if (raw.endsWith('s')) return parseFloat(raw);
  return parseFloat(raw) || 0;
}

/** Read a CSS custom property as a raw number (px, ratio — unit stripped). */
function num(css, token) {
  return parseFloat(css.getPropertyValue(token)) || 0;
}

/**
 * The single motion vocabulary. Values are pulled from motion.css, so editing
 * a token there changes both engines at once.
 *
 * Mutated in place by refreshMotionTokens() rather than replaced, so modules
 * that captured a reference at import time keep seeing current values when
 * the lab toolbar toggles reduced motion.
 *
 * Easing note: these GSAP names and the CSS cubic-beziers in motion.css are
 * deliberately matched pairs. Don't change one without the other.
 */
export const MOTION = {
  dur: {},
  ease: {
    standard: 'power2.inOut',
    entrance: 'expo.out',
    exit: 'expo.in',
    emphasis: 'expo.inOut',
    spring: 'back.out(1.7)',
    linear: 'none',
  },
  rise: {},
  scale: {},
  stagger: {},
  parallax: {},
};

/* ---------------------------------------------------------------------------
   Token overrides
   The Vocabulary playground on the index page lets you drag a duration and
   watch it land. Those edits are written here as inline custom properties on
   :root and mirrored to localStorage, so a tuning session carries across every
   page in the lab — tune the entrance curve on the index, then go look at it
   on the PDP without editing a file.

   This is deliberately a review layer, not a second source of truth. Nothing
   here changes motion.css. When a value is settled you copy it back into the
   stylesheet from the playground's export button, and clear the overrides.
   ------------------------------------------------------------------------ */
const OVERRIDE_KEY = 'figs-motion-token-overrides';

/** All tunable tokens, grouped the way the playground presents them. */
export const TUNABLE = {
  dur: ['--mo-instant', '--mo-quick', '--mo-base', '--mo-slow', '--mo-deliberate', '--mo-cinematic'],
  rise: ['--mo-rise-xs', '--mo-rise-sm', '--mo-rise-md', '--mo-rise-lg'],
  scale: ['--mo-scale-in', '--mo-scale-press', '--mo-scale-hover'],
  stagger: ['--mo-stagger-tight', '--mo-stagger-base', '--mo-stagger-loose'],
  parallax: ['--mo-parallax-near', '--mo-parallax-mid', '--mo-parallax-far'],
};

export function readOverrides() {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDE_KEY)) || {};
  } catch {
    return {};
  }
}

/** Write one token. Passing null removes it and falls back to motion.css. */
export function setOverride(token, value) {
  const all = readOverrides();
  if (value === null) delete all[token];
  else all[token] = value;

  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(all));
  } catch {
    /* private browsing — the override still applies, it just won't persist */
  }

  if (value === null) document.documentElement.style.removeProperty(token);
  else document.documentElement.style.setProperty(token, value);

  refreshMotionTokens();
  return all;
}

export function clearOverrides() {
  Object.keys(readOverrides()).forEach((token) =>
    document.documentElement.style.removeProperty(token)
  );
  try {
    localStorage.removeItem(OVERRIDE_KEY);
  } catch {
    /* no-op */
  }
  refreshMotionTokens();
}

/** Re-apply stored overrides. Called on every page before the first read. */
export function applyOverrides() {
  const all = readOverrides();
  Object.entries(all).forEach(([token, value]) =>
    document.documentElement.style.setProperty(token, value)
  );
  return all;
}

export function hasOverrides() {
  return Object.keys(readOverrides()).length > 0;
}

/** Strip the inline properties without forgetting them. */
function suspendOverrides() {
  Object.keys(readOverrides()).forEach((token) =>
    document.documentElement.style.removeProperty(token)
  );
}

/**
 * Overrides live in the :root style attribute, which outranks every stylesheet
 * rule — including the reduced-motion block. Left alone, a tuned duration
 * would quietly defeat the Reduced toggle and the fallback would review wrong.
 * So overrides are suspended whenever reduced motion is active and restored
 * when it's switched back off.
 */
export function syncOverrides() {
  if (isReduced()) suspendOverrides();
  else applyOverrides();
  refreshMotionTokens();
}

export function refreshMotionTokens() {
  const css = getComputedStyle(document.documentElement);

  Object.assign(MOTION.dur, {
    instant: seconds(css, '--mo-instant'),
    quick: seconds(css, '--mo-quick'),
    base: seconds(css, '--mo-base'),
    slow: seconds(css, '--mo-slow'),
    deliberate: seconds(css, '--mo-deliberate'),
    cinematic: seconds(css, '--mo-cinematic'),
  });
  Object.assign(MOTION.rise, {
    xs: num(css, '--mo-rise-xs'),
    sm: num(css, '--mo-rise-sm'),
    md: num(css, '--mo-rise-md'),
    lg: num(css, '--mo-rise-lg'),
  });
  Object.assign(MOTION.scale, {
    in: num(css, '--mo-scale-in'),
    press: num(css, '--mo-scale-press'),
    hover: num(css, '--mo-scale-hover'),
  });
  Object.assign(MOTION.stagger, {
    tight: seconds(css, '--mo-stagger-tight'),
    base: seconds(css, '--mo-stagger-base'),
    loose: seconds(css, '--mo-stagger-loose'),
  });
  Object.assign(MOTION.parallax, {
    near: num(css, '--mo-parallax-near'),
    mid: num(css, '--mo-parallax-mid'),
    far: num(css, '--mo-parallax-far'),
  });

  return MOTION;
}

syncOverrides();

/**
 * True when motion should be suppressed — either the OS setting or the lab
 * toolbar's "Reduced" toggle. Experiments check this to skip pinning and
 * scrub entirely rather than just running it fast.
 */
export function isReduced() {
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.dataset.moReduced === 'true'
  );
}

/**
 * Initialise GSAP for an experiment page.
 * Returns { gsap, ScrollTrigger, MOTION } or throws a readable error if the
 * CDN didn't load — better a console message than a silently blank page.
 */
export function initMotion() {
  const { gsap, ScrollTrigger } = window;

  if (!gsap) {
    throw new Error(
      '[motion] GSAP not found. Check the pinned CDN <script> tags in this page’s <head>.'
    );
  }
  if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  // Overrides first, so the very first read already reflects anything tuned in
  // the playground — unless reduced motion is on, which suspends them.
  syncOverrides();
  gsap.defaults({
    duration: MOTION.dur.base,
    ease: MOTION.ease.entrance,
  });

  // Elements marked [data-mo-reveal] are hidden by .mo-ready in motion.css.
  // The class is only added once GSAP is confirmed working, so a CDN failure
  // degrades to a plain, fully visible page instead of a blank one.
  document.documentElement.classList.add('mo-ready');

  // Re-read tokens whenever the reduced-motion state flips, then let the page
  // rebuild its timeline.
  window.addEventListener('mo:replay', () => {
    syncOverrides();
    gsap.defaults({
      duration: isReduced() ? MOTION.dur.instant : MOTION.dur.base,
      ease: isReduced() ? 'none' : MOTION.ease.entrance,
    });
  });

  return { gsap, ScrollTrigger, MOTION };
}

/**
 * Re-measure every ScrollTrigger once late-arriving layout settles (web fonts
 * swapping in, images decoding). Skipping this is the #1 cause of "the pin
 * starts in the wrong place on a hard refresh."
 */
export function refreshOnSettle() {
  const refresh = () => window.ScrollTrigger?.refresh();
  window.addEventListener('load', refresh);
  if (document.fonts?.ready) document.fonts.ready.then(refresh);
}
